<template>
    <!-- the season of a player: the key figures, the shooting and the rest, per game or in total -->
    <section class="cl-section player-season">
        <section-heading title="Στατιστικά σεζόν" icon="mdi-chart-box-outline">
            <match-type-choice light :options="['Μέσοι Όροι', 'Σύνολο']" @setType="value => option = value" />
        </section-heading>

        <div class="player-season__tiles">
            <div v-for="(tile, index) in tiles" :key="tile.key" class="player-season__tile" :class="{ 'player-season__tile--lead': index === 0 }">
                <span class="player-season__value">{{ format(stats[tile.key]) }}</span>
                <span class="player-season__code">{{ tile.code }}</span>
                <span class="player-season__label">{{ tile.label }}</span>
            </div>
        </div>

        <div class="player-season__row">
            <!-- shooting: the percentage as a bar, made / attempted under it -->
            <div class="player-season__panel player-season__shooting">
                <div class="player-season__panel-title">Σουτ</div>
                <div v-for="shot in shots" :key="shot.code" class="player-season__shot">
                    <div class="player-season__shot-head">
                        <span class="player-season__shot-code">{{ shot.code }}</span>
                        <span class="player-season__shot-pct">{{ pct(shot) }}%</span>
                    </div>
                    <div class="player-season__bar">
                        <span class="player-season__bar-fill" :style="{ width: `${Math.min(pct(shot), 100)}%` }" />
                    </div>
                    <div class="player-season__shot-ratio">{{ format(stats[shot.made]) }} / {{ format(stats[shot.attempts]) }} {{ option ? 'εύστοχα' : 'ανά αγώνα' }}</div>
                </div>
            </div>

            <!-- the rest of the line -->
            <div class="player-season__panel player-season__more">
                <div class="player-season__panel-title">Περισσότερα</div>
                <div class="player-season__chips">
                    <div v-for="item in more" :key="item.key" class="player-season__chip">
                        <span class="player-season__chip-value">{{ item.key === 'playTimeInSecs' ? minutes(stats[item.key]) : format(stats[item.key]) }}</span>
                        <span class="player-season__chip-label">{{ item.label }}</span>
                    </div>
                </div>
            </div>
        </div>
    </section>
</template>

<script>
    import SectionHeading from '@/components/SectionHeading'
    import MatchTypeChoice from '@/components/MatchTypeChoice'

    export default {
        components: { SectionHeading, MatchTypeChoice },
        props: {
            totalStats: {
                type: Object,
                default: null
            },
            avgStats: {
                type: Object,
                default: null
            }
        },
        data() {
            return {
                option: 0,
                tiles: [
                    { key: 'points', code: 'PTS', label: 'Πόντοι' },
                    { key: 'rebounds', code: 'REB', label: 'Ριμπάουντ' },
                    { key: 'assists', code: 'AST', label: 'Ασίστ' },
                    { key: 'steals', code: 'STL', label: 'Κλεψίματα' },
                    { key: 'blockedShots', code: 'BLK', label: 'Τάπες' },
                    { key: 'pir', code: 'EFF', label: 'Efficiency' }
                ],
                shots: [
                    { code: '2P', made: 'twoPointsMade', attempts: 'twoPointAttempts' },
                    { code: '3P', made: 'threePointsMade', attempts: 'threePointAttempts' },
                    { code: 'FT', made: 'freeThrowsMade', attempts: 'freeThrowAttempts' }
                ],
                more: [
                    { key: 'playTimeInSecs', label: 'Λεπτά' },
                    { key: 'offensiveRebounds', label: 'Επ. ριμπάουντ' },
                    { key: 'defensiveRebounds', label: 'Αμ. ριμπάουντ' },
                    { key: 'turnovers', label: 'Λάθη' },
                    // every foul: personal, technical and unsportsmanlike
                    { key: 'fouls', label: 'Φάουλ' }
                ]
            }
        },
        computed: {
            stats() {
                return (this.option ? this.totalStats : this.avgStats) || {}
            }
        },
        methods: {
            format(value) {
                const n = Number(value) || 0
                return this.option || Number.isInteger(n) ? n : n.toFixed(1)
            },
            pct(shot) {
                const made = Number(this.stats[shot.made]) || 0
                const attempts = Number(this.stats[shot.attempts]) || 0
                return attempts > 0 ? Math.round((made / attempts) * 1000) / 10 : 0
            },
            minutes(secs) {
                const value = Number(secs) || 0
                if (!value) return '–'
                const m = Math.floor(value / 60)
                const s = Math.floor(value % 60)
                return `${m}:${s < 10 ? '0' : ''}${s}`
            }
        }
    }
</script>

<style scoped>
.player-season {
    text-align: left;
}

.player-season__tiles {
    display: grid;
    grid-template-columns: repeat(6, minmax(0, 1fr));
    gap: 12px;
}

.player-season__tile {
    position: relative;
    display: flex;
    flex-direction: column;
    align-items: center;
    gap: 2px;
    padding: 18px 10px 16px;
    border-radius: 12px;
    background: #232323;
    color: #fff;
    box-shadow: 0 6px 20px rgba(35, 35, 35, 0.12);
    overflow: hidden;
}

.player-season__tile::after {
    content: '';
    position: absolute;
    left: 0;
    right: 0;
    bottom: 0;
    height: 3px;
    background: linear-gradient(90deg, #03e5b7 0%, #037ade 74%);
    opacity: 0.6;
}

.player-season__tile--lead {
    background: linear-gradient(315deg, #03e5b7 0%, #037ade 74%);
}

.player-season__tile--lead::after {
    display: none;
}

.player-season__value {
    font-family: 'Squada One', 'Roboto Condensed', Arial, sans-serif;
    font-size: 2.6rem;
    line-height: 1;
}

.player-season__code {
    font-family: Roboto, Arial, sans-serif;
    font-size: 0.8rem;
    font-weight: 800;
    letter-spacing: 0.1em;
    color: #03e5b7;
}

.player-season__tile--lead .player-season__code {
    color: #fff;
}

.player-season__label {
    font-family: Roboto, Arial, sans-serif;
    font-size: 0.72rem;
    color: rgba(255, 255, 255, 0.6);
}

.player-season__tile--lead .player-season__label {
    color: rgba(255, 255, 255, 0.85);
}

.player-season__row {
    display: grid;
    grid-template-columns: minmax(0, 1.4fr) minmax(0, 1fr);
    gap: 12px;
    margin-top: 12px;
}

.player-season__panel {
    padding: 16px 18px;
    border-radius: 12px;
    background: #232323;
    color: #fff;
    box-shadow: 0 6px 20px rgba(35, 35, 35, 0.12);
    font-family: Roboto, Arial, sans-serif;
}

.player-season__panel-title {
    margin-bottom: 12px;
    font-family: 'Squada One', 'Roboto Condensed', Arial, sans-serif;
    font-size: 1.3rem;
    letter-spacing: 0.03em;
    text-transform: uppercase;
}

.player-season__shooting {
    display: grid;
    grid-template-columns: repeat(3, minmax(0, 1fr));
    column-gap: 18px;
}

.player-season__shooting .player-season__panel-title {
    grid-column: 1 / -1;
}

.player-season__shot-head {
    display: flex;
    align-items: baseline;
    justify-content: space-between;
}

.player-season__shot-code {
    font-size: 0.8rem;
    font-weight: 800;
    letter-spacing: 0.1em;
    color: #03e5b7;
}

.player-season__shot-pct {
    font-family: 'Squada One', 'Roboto Condensed', Arial, sans-serif;
    font-size: 1.8rem;
    line-height: 1;
}

.player-season__bar {
    height: 6px;
    margin: 8px 0 6px;
    border-radius: 999px;
    background: rgba(255, 255, 255, 0.1);
    overflow: hidden;
}

.player-season__bar-fill {
    display: block;
    height: 100%;
    border-radius: 999px;
    background: linear-gradient(90deg, #03e5b7 0%, #037ade 100%);
}

.player-season__shot-ratio {
    font-size: 0.72rem;
    color: rgba(255, 255, 255, 0.55);
}

.player-season__chips {
    display: grid;
    grid-template-columns: repeat(auto-fill, minmax(84px, 1fr));
    gap: 8px;
}

.player-season__chip {
    display: flex;
    flex-direction: column;
    align-items: center;
    padding: 8px 6px;
    border-radius: 8px;
    background: rgba(255, 255, 255, 0.06);
    border: 1px solid rgba(255, 255, 255, 0.08);
}

.player-season__chip-value {
    font-family: 'Squada One', 'Roboto Condensed', Arial, sans-serif;
    font-size: 1.4rem;
    line-height: 1;
}

.player-season__chip-label {
    margin-top: 3px;
    font-size: 0.66rem;
    color: rgba(255, 255, 255, 0.55);
    text-align: center;
}

@media (max-width: 900px) {
    .player-season__tiles {
        grid-template-columns: repeat(3, minmax(0, 1fr));
        gap: 8px;
    }

    .player-season__tile {
        padding: 12px 6px;
    }

    .player-season__value {
        font-size: 2rem;
    }

    .player-season__row {
        grid-template-columns: 1fr;
    }
}

@media (max-width: 500px) {
    .player-season__shooting {
        column-gap: 10px;
        padding: 14px 12px;
    }

    .player-season__shot-pct {
        font-size: 1.4rem;
    }

    .player-season__more {
        padding: 14px 12px;
    }

    .player-season__chips {
        grid-template-columns: repeat(5, minmax(0, 1fr));
        gap: 6px;
    }

    .player-season__chip {
        padding: 8px 2px;
    }

    .player-season__chip-value {
        font-size: 1.15rem;
    }

    .player-season__chip-label {
        font-size: 0.58rem;
    }
}
</style>
